import { z } from 'zod';
import { foodGroupSchema, foodSourceSchema } from './clinicNutrition.js';
/**
 * Plano alimentar (N2, estudo do nutricionista de 23/10). Refeições com horário, cada uma
 * com opções ("escolha da semana") e cada opção com os alimentos em medida caseira. Cada
 * item guarda os números do alimento na hora da prescrição: o plano publicado não muda se a
 * base mudar. O plano publicado vai para a Saúde do paciente; o anterior sai.
 */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data no formato AAAA-MM-DD');
const isoDateTime = z.iso.datetime();
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Horário no formato HH:MM');
export const MEAL_PLAN_LIMITS = { meals: 10, options: 6, items: 30, note: 600 };
/** Números por 100 g, copiados do alimento na hora de pôr no plano. */
export const mealPlanPer100Schema = z.object({
    kcal: z.number().min(0).max(1000),
    protein: z.number().min(0).max(100),
    carb: z.number().min(0).max(100),
    fat: z.number().min(0).max(100),
    fiber: z.number().min(0).max(100).nullable(),
    sodiumMg: z.number().min(0).max(40000).nullable(),
});
export const mealPlanItemSchema = z.object({
    id: z.string().min(1).max(40),
    foodId: z.uuid().nullable(),
    name: z.string().trim().min(1).max(120),
    source: foodSourceSchema,
    group: foodGroupSchema,
    per100: mealPlanPer100Schema,
    /** Medida caseira escolhida; sem ela, a quantidade é em gramas. */
    measure: z
        .object({ label: z.string().min(1).max(40), grams: z.number().min(0.1).max(5000) })
        .nullable(),
    quantity: z.number().min(0.05).max(5000),
    note: z.string().trim().max(120).optional(),
});
export const mealPlanOptionSchema = z.object({
    id: z.string().min(1).max(40),
    items: z.array(mealPlanItemSchema).max(MEAL_PLAN_LIMITS.items),
    note: z.string().trim().max(MEAL_PLAN_LIMITS.note).optional(),
});
export const mealPlanMealSchema = z.object({
    id: z.string().min(1).max(40),
    name: z.string().trim().min(1).max(40),
    time: hhmm.nullable(),
    options: z.array(mealPlanOptionSchema).min(1).max(MEAL_PLAN_LIMITS.options),
});
export const mealPlanContentSchema = z.object({
    meals: z.array(mealPlanMealSchema).max(MEAL_PLAN_LIMITS.meals),
    /** Orientações gerais (água, mastigação, substituições). */
    guidance: z.string().trim().max(3000).default(''),
});
export const MEAL_PLAN_STATUSES = ['rascunho', 'publicado', 'arquivado'];
export const mealPlanStatusSchema = z.enum(MEAL_PLAN_STATUSES);
export const mealPlanSchema = z.object({
    id: z.uuid(),
    patientId: z.uuid().nullable(),
    /** Modelo da clínica (sem paciente), para duplicar. */
    template: z.boolean(),
    title: z.string(),
    status: mealPlanStatusSchema,
    startsOn: isoDate.nullable(),
    content: mealPlanContentSchema,
    /** A meta de kcal do paciente quando o plano foi salvo, para comparar. */
    targetKcal: z.number().nullable(),
    author: z.string(),
    updatedAt: isoDateTime,
    publishedAt: isoDateTime.nullable(),
});
export const mealPlanSummarySchema = mealPlanSchema.omit({ content: true }).extend({
    meals: z.number().int(),
    kcal: z.number(),
});
export const saveMealPlanSchema = z.object({
    title: z.string().trim().min(1).max(80),
    startsOn: isoDate.nullable().optional(),
    content: mealPlanContentSchema,
});
export const createMealPlanSchema = saveMealPlanSchema
    .partial({ content: true, title: true })
    .extend({
    /** Começar de um modelo ou de outro plano (duplicar). */
    fromId: z.uuid().optional(),
    /** Sem paciente, nasce um modelo da clínica. */
    patientId: z.uuid().nullable().optional(),
});
/** Refeições de partida de um plano novo. */
export const DEFAULT_MEALS = [
    { name: 'Café da manhã', time: '07:30' },
    { name: 'Lanche da manhã', time: '10:00' },
    { name: 'Almoço', time: '12:30' },
    { name: 'Lanche da tarde', time: '16:00' },
    { name: 'Jantar', time: '19:30' },
    { name: 'Ceia', time: '22:00' },
];
const round = (value, places = 0) => {
    const step = 10 ** places;
    return Math.round(value * step) / step;
};
const ZERO = { kcal: 0, protein: 0, carb: 0, fat: 0, fiber: 0, sodiumMg: 0 };
/** Gramas do item: quantidade × medida (ou gramas direto). */
export function itemGrams(item) {
    return item.measure ? item.measure.grams * item.quantity : item.quantity;
}
export function itemMacros(item) {
    const k = itemGrams(item) / 100;
    return {
        kcal: item.per100.kcal * k,
        protein: item.per100.protein * k,
        carb: item.per100.carb * k,
        fat: item.per100.fat * k,
        fiber: (item.per100.fiber ?? 0) * k,
        sodiumMg: (item.per100.sodiumMg ?? 0) * k,
    };
}
const add = (a, b) => ({
    kcal: a.kcal + b.kcal,
    protein: a.protein + b.protein,
    carb: a.carb + b.carb,
    fat: a.fat + b.fat,
    fiber: a.fiber + b.fiber,
    sodiumMg: a.sodiumMg + b.sodiumMg,
});
export function optionMacros(option) {
    return option.items.reduce((sum, item) => add(sum, itemMacros(item)), ZERO);
}
export function roundMacros(m) {
    return {
        kcal: round(m.kcal),
        protein: round(m.protein, 1),
        carb: round(m.carb, 1),
        fat: round(m.fat, 1),
        fiber: round(m.fiber, 1),
        sodiumMg: round(m.sodiumMg),
    };
}
/**
 * Total do dia: soma a opção escolhida de cada refeição (a primeira, se não houver
 * escolha) e dá também a faixa entre a opção mais leve e a mais pesada.
 */
export function planTotals(content, chosen = {}) {
    let day = ZERO;
    let min = 0;
    let max = 0;
    for (const meal of content.meals) {
        const options = meal.options.map((option) => ({ id: option.id, macros: optionMacros(option) }));
        const pick = options.find((option) => option.id === chosen[meal.id]) ?? options[0];
        if (pick)
            day = add(day, pick.macros);
        const kcals = options.map((option) => option.macros.kcal);
        if (kcals.length > 0) {
            min += Math.min(...kcals);
            max += Math.max(...kcals);
        }
    }
    return { day: roundMacros(day), min: round(min), max: round(max) };
}
/** Plural de uma palavra: "colher" → "colheres", "unidade" → "unidades", "pão" → "pães". */
function pluralWord(word) {
    if (/ão$/.test(word))
        return word === 'pão' ? 'pães' : `${word.slice(0, -2)}ões`;
    if (/[rsz]$/.test(word))
        return `${word}es`;
    if (/l$/.test(word))
        return `${word.slice(0, -1)}is`;
    if (/m$/.test(word))
        return `${word.slice(0, -1)}ns`;
    return `${word}s`;
}
const ADJECTIVES = /^(pequena|pequeno|média|médio|grande|cheia|cheio|rasa|raso|fundo|funda)$/;
/**
 * O nome da medida na quantidade: "2 colheres de sopa", "1,5 unidade média" (fração fica
 * no singular), "3 unidades grandes". Só o substantivo e o adjetivo colado mudam.
 */
export function measurePhrase(label, quantity) {
    const words = label.toLowerCase().split(' ');
    if (quantity < 2 || words.length === 0)
        return words.join(' ');
    words[0] = pluralWord(words[0]);
    if (words[1] && ADJECTIVES.test(words[1]))
        words[1] = pluralWord(words[1]);
    return words.join(' ');
}
/** "2 colheres de sopa (40 g)" / "150 g": como o item aparece no plano e no PDF. */
export function itemAmountText(item) {
    const n = (value) => value.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
    if (!item.measure)
        return `${n(item.quantity)} g`;
    return `${n(item.quantity)} ${measurePhrase(item.measure.label, item.quantity)} (${n(itemGrams(item))} g)`;
}
/** Medidas que se compram ou servem por contagem, não por peso. */
const COUNTED = /^(unidade|fatia|xícara|copo|lata|garrafa|caixa|pacote|pote|embalagem|porção do rótulo)/i;
/** Lista de compras da semana: o total de cada alimento nas opções escolhidas, por 7 dias. */
export function shoppingList(content, chosen = {}, days = 7) {
    const totals = new Map();
    for (const meal of content.meals) {
        const option = meal.options.find((item) => item.id === chosen[meal.id]) ?? meal.options[0];
        for (const item of option?.items ?? []) {
            const key = (item.foodId ?? item.name).toLowerCase();
            const found = totals.get(key) ??
                totals
                    .set(key, {
                    name: item.name,
                    group: item.group,
                    grams: 0,
                    count: null,
                    labels: new Set(),
                    quantity: 0,
                })
                    .get(key);
            found.grams += itemGrams(item) * days;
            found.labels.add(item.measure?.label ?? 'g');
            found.quantity += item.quantity * days;
        }
    }
    return [...totals.values()]
        .map(({ labels, quantity, ...row }) => {
        const [label] = [...labels];
        const counted = labels.size === 1 && label && label !== 'g' && COUNTED.test(label);
        return {
            ...row,
            grams: round(row.grams),
            count: counted ? { quantity: round(quantity, 1), label: label } : null,
        };
    })
        .sort((a, b) => a.group.localeCompare(b.group) || a.name.localeCompare(b.name, 'pt-BR'));
}
// ── Saúde: o plano do lado do paciente ─────────────────────────────────────
export const patientMealPlanSchema = z.object({
    plan: mealPlanSchema.nullable(),
    /** A opção que a pessoa escolheu para a semana em cada refeição. */
    chosen: z.record(z.string(), z.string()),
    clinicName: z.string(),
});
export const chooseMealOptionSchema = z.object({
    mealId: z.string().min(1).max(40),
    optionId: z.string().min(1).max(40),
});
